/* Cognito 실연동 — SRP 로그인 → developer 그룹 확인 → Identity Pool STS.
   plan.md §4 / Huenit lib/auth/cognito_auth_service.dart 흐름을 웹으로 옮긴 것.

   이 모듈은 session.jsx(live 분기)에서만 호출된다. fake 모드(env 미설정)에선
   import 만 되고 어떤 것도 생성하지 않는다 — 풀/네트워크는 첫 login/restore 시
   지연 생성.

   ★ 토큰 보관: amazon-cognito-identity-js 가 토큰을 storage 에 캐시한다. 기본은
   localStorage 이나 plan §4.4 가 요구하는 "탭 종료 시 소멸"을 위해 풀·유저 모두
   sessionStorage 로 강제한다(CognitoUser 는 pool.storage 를 자동 상속하지 않으므로
   명시 전달). 비번 평문 저장 없음 — refresh token 만 탭 세션 동안 영속. */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
// 주의: 배럴 패키지 '@aws-sdk/credential-providers' 는 node:fs/crypto 에 의존하는
// 비-브라우저 provider(fromLogin/fromIni 등)까지 끌어와 Vite 빌드를 깬다. 동일
// 함수를 정의하는 전용 서브패키지에서 직접 import 해 브라우저 안전 경로만 번들한다.
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import {
  AWS_REGION,
  COGNITO_USER_POOL_ID,
  COGNITO_CLIENT_ID,
  COGNITO_IDENTITY_POOL_ID,
} from '../config/env.js';

const REQUIRED_GROUP = 'developer';
const REFRESH_WINDOW_MS = 5 * 60 * 1000;   // ID token 만료 5분 전부터 선제 갱신
const LOGINS_KEY = `cognito-idp.${AWS_REGION}.amazonaws.com/${COGNITO_USER_POOL_ID}`;

const STORAGE = typeof window !== 'undefined' ? window.sessionStorage : undefined;

/* cognito:groups 추출 — 정상 ID token 은 배열이지만 직렬화 변형(대괄호/공백/콤마
   문자열)도 admin-api Lambda _groups() 와 동일하게 방어적으로 파싱. */
function groupsOf(idToken) {
  let g;
  try { g = idToken.decodePayload()['cognito:groups']; } catch (e) { return []; }
  if (Array.isArray(g)) return g.map(String);
  if (typeof g === 'string') {
    return g.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
  }
  return [];
}

function emailOf(idToken) {
  try {
    const p = idToken.decodePayload();
    return (typeof p.email === 'string' && p.email) || null;
  } catch (e) { return null; }
}

/* Cognito 에러 코드 → 운영자에게 보여줄 한국어 메시지. 권한/자격/네트워크 구분
   (plan: LoginScreen 이 셋을 구분 표시). */
function friendly(err) {
  const code = err && (err.code || err.name);
  switch (code) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return '이메일 또는 비밀번호가 올바르지 않습니다.';
    case 'UserNotConfirmedException':
      return '계정이 아직 확인되지 않았습니다. 관리자에게 문의하세요.';
    case 'PasswordResetRequiredException':
      return '비밀번호 재설정이 필요합니다.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return '요청이 많습니다. 잠시 후 다시 시도하세요.';
    case 'NetworkError':
      return '네트워크 오류 — 연결 상태를 확인하세요.';
    default: {
      const m = err && err.message ? String(err.message) : '';
      if (/network|fetch|Failed to fetch/i.test(m)) {
        return '네트워크 오류 — 연결 상태를 확인하세요.';
      }
      return m || '로그인에 실패했습니다.';
    }
  }
}

class CognitoAuth {
  constructor() {
    this._pool = null;
    this._user = null;
    this._email = null;
    this._creds = null;        // { value, expMs }
  }

  pool() {
    if (!this._pool) {
      this._pool = new CognitoUserPool({
        UserPoolId: COGNITO_USER_POOL_ID,
        ClientId: COGNITO_CLIENT_ID,
        Storage: STORAGE,
      });
    }
    return this._pool;
  }

  _reset() {
    this._user = null;
    this._email = null;
    this._creds = null;
  }

  // ── callback API → Promise 래퍼 ───────────────────────────────────
  _authenticate(user, details) {
    return new Promise((resolve, reject) => {
      user.authenticateUser(details, {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
        // 강제 비번변경/ MFA 계정은 현 콘솔이 미지원 — 명확히 거부.
        newPasswordRequired: () =>
          reject(new Error('비밀번호 변경이 필요한 계정입니다. 먼저 비밀번호를 설정하세요.')),
        mfaRequired: () =>
          reject(new Error('MFA 가 설정된 계정입니다. 현재 콘솔은 MFA 로그인을 지원하지 않습니다.')),
        totpRequired: () =>
          reject(new Error('MFA 가 설정된 계정입니다. 현재 콘솔은 MFA 로그인을 지원하지 않습니다.')),
      });
    });
  }

  _getSession(user) {
    // getSession 은 만료된 토큰을 refresh token 으로 자동 갱신한다.
    return new Promise((resolve, reject) => {
      user.getSession((err, session) => (err ? reject(err) : resolve(session)));
    });
  }

  _refreshSession(user, refreshToken) {
    return new Promise((resolve, reject) => {
      user.refreshSession(refreshToken, (err, session) =>
        (err ? reject(err) : resolve(session)));
    });
  }

  // 선제 갱신: 세션이 유효하더라도 만료 5분 이내면 refresh token 으로 갱신 시도.
  async _maybeRefresh(user, session) {
    const expMs = session.getIdToken().getExpiration() * 1000;
    if (expMs - Date.now() >= REFRESH_WINDOW_MS) return session;
    try {
      const rt = session.getRefreshToken();
      if (!rt || !rt.getToken()) return session;
      const next = await this._refreshSession(user, rt);
      return next && next.isValid() ? next : session;
    } catch (e) {
      return session;   // 갱신 실패 — 아직 만료 전이면 현 토큰 유지(다음 호출에서 재시도)
    }
  }

  // ── 공개 API (session.jsx 가 소비) ────────────────────────────────

  /** SRP 로그인 → developer 그룹 확인. 실패/비-developer면 throw. */
  async login(email, password) {
    const user = new CognitoUser({ Username: email, Pool: this.pool(), Storage: STORAGE });
    user.setAuthenticationFlowType('USER_SRP_AUTH');
    let session;
    try {
      session = await this._authenticate(
        user,
        new AuthenticationDetails({ Username: email, Password: password }),
      );
    } catch (err) {
      throw new Error(friendly(err));
    }
    if (!session || !session.isValid()) {
      throw new Error('로그인 세션이 유효하지 않습니다. 다시 시도하세요.');
    }
    if (!groupsOf(session.getIdToken()).includes(REQUIRED_GROUP)) {
      try { user.signOut(); } catch (e) { /* ignore */ }
      this._reset();
      throw new Error('개발자 권한이 필요합니다. 이 계정으로는 콘솔에 접근할 수 없습니다.');
    }
    this._user = user;
    this._email = emailOf(session.getIdToken()) || email;
    this._creds = null;
    return { email: this._email, isDeveloper: true };
  }

  /** 새로고침 후 sessionStorage 의 토큰으로 세션 복원. 만료·미갱신·비-developer면
      세션을 비우고 null 반환 → 재로그인 유도. */
  async restore() {
    const user = this.pool().getCurrentUser();
    if (!user) return null;
    let session;
    try {
      session = await this._getSession(user);
    } catch (e) {
      try { user.signOut(); } catch (e2) { /* ignore */ }
      return null;
    }
    if (!session || !session.isValid()) {
      try { user.signOut(); } catch (e) { /* ignore */ }
      return null;
    }
    if (!groupsOf(session.getIdToken()).includes(REQUIRED_GROUP)) {
      try { user.signOut(); } catch (e) { /* ignore */ }
      return null;
    }
    this._user = user;
    this._email = emailOf(session.getIdToken()) || user.getUsername();
    this._creds = null;
    return { email: this._email, isDeveloper: true };
  }

  /** 토큰·캐시 자격 폐기. signOut 이 sessionStorage 토큰을 비운다. */
  logout() {
    const user = this._user || this.pool().getCurrentUser();
    try { if (user) user.signOut(); } catch (e) { /* ignore */ }
    this._reset();
  }

  /** 신선한 ID token(만료 임박 시 갱신). 갱신 불가/세션 없음이면 null. */
  async getIdToken() {
    const user = this._user || this.pool().getCurrentUser();
    if (!user) return null;
    this._user = user;
    let session;
    try {
      session = await this._getSession(user);
    } catch (e) {
      return null;   // refresh token 만료/소실 → 호출처가 재로그인 유도
    }
    if (!session || !session.isValid()) return null;
    const fresh = await this._maybeRefresh(user, session);
    return fresh.getIdToken().getJwtToken();
  }

  /** S3(SigV4)용 STS 임시 자격. 캐시 후 만료 5분 전 재발급. 세션 없으면 null.
      반환 shape = 동결 인터페이스({accessKeyId,secretAccessKey,sessionToken,region}). */
  async getCredentials() {
    const cached = this._creds;
    if (cached && cached.expMs - Date.now() > REFRESH_WINDOW_MS) {
      return cached.value;
    }
    const idToken = await this.getIdToken();
    if (!idToken) return null;
    const provider = fromCognitoIdentityPool({
      identityPoolId: COGNITO_IDENTITY_POOL_ID,
      logins: { [LOGINS_KEY]: idToken },
      clientConfig: { region: AWS_REGION },
    });
    const c = await provider();
    const expMs = c.expiration ? new Date(c.expiration).getTime() : Date.now() + 55 * 60 * 1000;
    const value = {
      accessKeyId: c.accessKeyId,
      secretAccessKey: c.secretAccessKey,
      sessionToken: c.sessionToken,
      region: AWS_REGION,
      expiration: new Date(expMs),
    };
    this._creds = { value, expMs };
    return value;
  }
}

export const cognito = new CognitoAuth();
export default cognito;
